import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * LegalDoc — renders a markdown legal document (MSA / DPA / AUP) as a styled
 * page. The markdown string is the SINGLE SOURCE OF TRUTH: the same file you
 * host here is the one clients agree to by email, so the hosted page and the
 * signed document never drift.
 *
 * Styling reuses the `prose` typography classes already present in this project
 * (the same ones TermsOfService.jsx / PrivacyPolicy.jsx use), so headings,
 * lists, bold, and tables render correctly with no extra CSS. remark-gfm is
 * what makes the DPA's markdown table render.
 *
 * To switch to the dark gradient look of TermsOfService.jsx instead of this
 * light theme, swap the two marked classNames below (see comments).
 */
const LegalDoc = ({ content }) => {
  return (
    // DARK THEME: replace "bg-slate-100" with
    // "bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-xl">
          {/* DARK THEME: change "prose prose-slate" to "prose prose-invert" */}
          <article
            className="prose prose-slate max-w-none
                       prose-headings:font-bold
                       prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:mb-2
                       prose-h2:text-2xl prose-h2:mt-10
                       prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                       prose-table:text-sm prose-td:align-top
                       prose-li:my-1"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </article>
        </div>

        <div className="text-center mt-8">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()}{' '}
            <span className="text-blue-600 font-semibold">EXITIUM Labs</span>. All
            rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalDoc;
